import React, { useCallback, useRef, useState } from 'react';
import { FlaskConical, Radio, Zap } from 'lucide-react';
import DemoScenarioItems from './DemoScenarioItems';
import { useDismiss } from './useDismiss';
import { useMenuKeys } from './useMenuKeys';
import { useUtcClock } from './useUtcClock';

interface ModeIndicatorProps {
  demo: boolean;
  /** The demo scenarios (Demo only). */
  onStorm?: () => void;
  onNeedle?: () => void;
  onReset?: () => void;
  /** A storm is running; the chip says so. */
  storming?: boolean;
}

/**
 * Demo or Live, and the time in UTC (item 62). In Live it is plain text: it is not a live region, so it does
 * not announce every minute. In Demo it is the button of the scenarios menu (Alert storm, Needle in the
 * haystack, Reset), built like the Settings and Account menus.
 */
const ModeIndicator: React.FC<ModeIndicatorProps> = ({ demo, onStorm, onNeedle, onReset, storming }) => {
  const clock = useUtcClock();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(rootRef, open, close, triggerRef);
  const keys = useMenuKeys(rootRef, open, setOpen);

  const Icon = storming ? Zap : demo ? FlaskConical : Radio;
  const word = storming ? 'Storm in progress…' : demo ? 'Demo' : 'Live';
  const content = (
    <>
      <Icon size={16} strokeWidth={1.5} aria-hidden />
      <span>{word}</span>
      <time className="c3-mode-time" dateTime={clock}>
        {clock} UTC
      </time>
    </>
  );

  if (!demo) {
    return (
      <span className="c3-mode is-live" title="Live mode. Figures come from your connected sources.">
        {content}
      </span>
    );
  }

  return (
    <div className="c3-nav-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="c3-mode is-demo"
        title="Demo mode. The metrics band shows sample figures. Open the demo scenarios."
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={keys.onTriggerKeyDown}
      >
        {content}
      </button>
      {open && (
        <div
          className="c3-nav-menu-panel c3-demo-menu"
          role="menu"
          aria-label="Demo scenarios"
          onKeyDown={keys.onMenuKeyDown}
          onClick={(e) => {
            // The menu goes away with its entries, so the focus returns to the chip first.
            if ((e.target as HTMLElement).closest('[role="menuitem"]')) {
              triggerRef.current?.focus();
              setOpen(false);
            }
          }}
        >
          <div className="c3-nav-menu-heading">Demo scenarios</div>
          <DemoScenarioItems
            onStorm={() => onStorm?.()}
            onNeedle={() => onNeedle?.()}
            onReset={() => onReset?.()}
            storming={storming}
          />
        </div>
      )}
    </div>
  );
};

export default ModeIndicator;
